import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, ArrowLeft, Building2, Loader2 } from 'lucide-react';
import { Button } from '../components/core/Button';
import { LanguageToggle } from '../components/core/LanguageToggle';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { initiatePayment } from '../api/subscription.api';
import { PLAN_PRICES, TIER_LIMITS } from '@app/common';
import type { PlanType, PurchasablePlan, SocialPlatform } from '@app/common';
import type { TranslationKey, TranslationParams } from '../i18n';

/** A feature bullet as a key plus its numbers, resolved at render time. */
interface PlanFeature {
  key: TranslationKey;
  params?: TranslationParams;
}

// Copy is held as translation keys rather than strings because this list is
// module-level and therefore evaluated once, before any language is known.
// `price` stays literal — a taka figure is the same in both languages.
interface Plan {
  id: 'free_trial' | PurchasablePlan | 'institution';
  nameKey: TranslationKey;
  price: string;
  periodKey: TranslationKey;
  badgeKey?: TranslationKey;
  badgeColor?: string;
  headlineKey: TranslationKey;
  headlineParams?: TranslationParams;
  features: PlanFeature[];
  ctaKey: TranslationKey;
  recommended?: boolean;
  contactOnly?: boolean;
}

/**
 * Feature bullets derived from the plan's own limits.
 *
 * Every number here comes from `TIER_LIMITS`, so the pricing page cannot claim a
 * quota the backend does not enforce. This page used to hardcode its numbers and
 * had to be hand-synced with `common/`, which is exactly the kind of drift that
 * ends with a customer paying for a limit they never receive.
 */
function featuresFor(plan: PlanType): PlanFeature[] {
  const limits = TIER_LIMITS[plan];
  const platformNames = limits.platforms
    .map((p) => PLATFORM_LABELS[p])
    .join(', ');

  const features: PlanFeature[] = [
    { key: 'pricing.featPosts', params: { count: limits.postsPerMonth.toLocaleString('en-BD') } },
    {
      key: 'pricing.featGenerations',
      params: { count: limits.aiGenerationsPerMonth.toLocaleString('en-BD') },
    },
    {
      key: limits.brands === 1 ? 'pricing.featBrands' : 'pricing.featBrandsPlural',
      params: { count: limits.brands },
    },
    { key: 'pricing.featAccounts', params: { count: limits.connectedAccounts } },
    { key: 'pricing.featPlatforms', params: { platforms: platformNames } },
    { key: 'pricing.featScheduleHorizon', params: { days: limits.scheduleHorizonDays } },
    {
      key: 'pricing.featStorage',
      params: {
        amount:
          limits.mediaStorageMb >= 1000
            ? `${limits.mediaStorageMb / 1000} GB`
            : `${limits.mediaStorageMb} MB`,
      },
    },
  ];

  if (limits.approvalWorkflow) features.push({ key: 'pricing.featApprovals' });
  if (plan === 'agency') features.push({ key: 'pricing.featWorkspaces' });

  return features;
}

// Platform display names are proper nouns, identical in both dictionaries, and
// this list is built at module scope before a language exists — so they are
// literals here rather than translation keys.
const PLATFORM_LABELS: Record<SocialPlatform, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

const PLANS: Plan[] = [
  {
    id: 'free_trial',
    nameKey: 'pricing.planFreeTrial',
    price: '৳0',
    periodKey: 'pricing.days14',
    badgeKey: 'pricing.badgeFree',
    badgeColor: 'bg-emerald-500',
    headlineKey: 'pricing.featPosts',
    headlineParams: { count: TIER_LIMITS.free_trial.postsPerMonth },
    features: featuresFor('free_trial'),
    ctaKey: 'pricing.ctaFreeTrial',
  },
  {
    id: 'starter',
    nameKey: 'pricing.planStarter',
    price: `৳${PLAN_PRICES.starter.BDT.toLocaleString('en-BD')}`,
    periodKey: 'pricing.perMonth',
    headlineKey: 'pricing.featPosts',
    headlineParams: { count: TIER_LIMITS.starter.postsPerMonth },
    features: featuresFor('starter'),
    ctaKey: 'pricing.ctaStarter',
  },
  {
    id: 'growth',
    nameKey: 'pricing.planGrowth',
    price: `৳${PLAN_PRICES.growth.BDT.toLocaleString('en-BD')}`,
    periodKey: 'pricing.perMonth',
    badgeKey: 'pricing.badgeBest',
    badgeColor: 'bg-violet-600',
    headlineKey: 'pricing.featPosts',
    headlineParams: { count: TIER_LIMITS.growth.postsPerMonth },
    features: featuresFor('growth'),
    ctaKey: 'pricing.ctaGrowth',
    recommended: true,
  },
  {
    id: 'agency',
    nameKey: 'pricing.planAgency',
    price: `৳${PLAN_PRICES.agency.BDT.toLocaleString('en-BD')}`,
    periodKey: 'pricing.perMonth',
    headlineKey: 'pricing.featPosts',
    headlineParams: { count: TIER_LIMITS.agency.postsPerMonth },
    features: featuresFor('agency'),
    ctaKey: 'pricing.ctaAgency',
  },
  {
    id: 'institution',
    nameKey: 'pricing.planEnterprise',
    price: 'Custom',
    periodKey: 'pricing.contactUs',
    headlineKey: 'pricing.featCustomVolume',
    features: [
      { key: 'pricing.featCustomVolume' },
      { key: 'pricing.featMultiUser' },
      { key: 'pricing.featSupport' },
      { key: 'pricing.featBranding' },
      { key: 'pricing.featInvoice' },
    ],
    ctaKey: 'pricing.ctaEnterprise',
    contactOnly: true,
  },
];

export function PricingPage() {
  const { user, signInWithGoogle } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [payLoading, setPayLoading] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  const handleCTA = async (plan: Plan) => {
    setPayError(null);

    if (plan.contactOnly) {
      window.location.href = 'mailto:developer@example.com?subject=Enterprise Plan Inquiry';
      return;
    }

    if (plan.id === 'free_trial') {
      if (!user) {
        await signInWithGoogle();
      } else {
        navigate('/');
      }
      return;
    }

    if (!user) {
      await signInWithGoogle();
      return;
    }

    setPayLoading(plan.id);
    try {
      const result = await initiatePayment(plan.id as PurchasablePlan);
      if (result.activated) {
        navigate(`/payment/success?tran_id=${result.tranId}`);
      } else if (result.gatewayUrl) {
        window.location.href = result.gatewayUrl;
      }
    } catch (err: unknown) {
      const e = err as { message?: string };
      setPayError(e.message ?? t('pricing.payFailed'));
    } finally {
      setPayLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      {/* Back */}
      <div className="max-w-6xl mx-auto mb-8 flex items-center justify-between gap-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </button>
        {/* Reachable without an account: /pricing is a public route. */}
        <LanguageToggle />
      </div>

      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-violet-600/20 border border-violet-500/30 rounded-full px-4 py-1.5 text-violet-300 text-sm font-medium mb-4">
          <Sparkles className="w-4 h-4" /> {t('pricing.badge')}
        </div>
        <h1 className="text-4xl font-bold text-white mb-3">{t('pricing.title')}</h1>
        <p className="text-slate-400 max-w-lg mx-auto">{t('pricing.subtitle')}</p>
      </div>

      {/* Plans grid */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {payError && (
          <div className="col-span-full mb-2 p-3 bg-red-900/30 border border-red-500/40 rounded-xl text-red-300 text-sm text-center">
            {payError}
          </div>
        )}
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`relative flex flex-col rounded-2xl border p-6 ${
              plan.recommended
                ? 'border-violet-500 bg-violet-600/10 shadow-violet-500/20 shadow-xl'
                : 'border-slate-700 bg-slate-800/60'
            }`}
          >
            {plan.badgeKey && (
              <div
                className={`absolute -top-3 left-1/2 -translate-x-1/2 ${plan.badgeColor} text-white text-xs font-semibold px-3 py-1 rounded-full`}
              >
                {t(plan.badgeKey)}
              </div>
            )}

            {/* Plan name + price */}
            <div className="mb-5">
              <h3 className="text-lg font-semibold text-white mb-1">{t(plan.nameKey)}</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-white">
                  {plan.contactOnly ? t('pricing.custom') : plan.price}
                </span>
                <span className="text-slate-400 text-sm">{t(plan.periodKey)}</span>
              </div>
              <div className="mt-2 text-violet-300 text-sm font-medium">{t(plan.headlineKey, plan.headlineParams)}</div>
            </div>

            {/* Features */}
            <ul className="space-y-2 mb-8 flex-1">
              {plan.features.map((f) => (
                <li key={f.key} className="flex items-start gap-2 text-slate-300 text-sm">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  {t(f.key, f.params)}
                </li>
              ))}
            </ul>

            {/* CTA */}
            {plan.contactOnly ? (
              <button
                onClick={() => handleCTA(plan)}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-slate-600 text-slate-300 hover:bg-slate-700 text-sm font-medium transition-colors"
              >
                <Building2 className="w-4 h-4" />
                {t(plan.ctaKey)}
              </button>
            ) : (
              <Button
                onClick={() => handleCTA(plan)}
                disabled={payLoading === plan.id}
                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  plan.recommended
                    ? 'bg-violet-600 hover:bg-violet-500 text-white'
                    : 'bg-slate-700 hover:bg-slate-600 text-white'
                }`}
              >
                {payLoading === plan.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> {t('common.processing')}
                  </span>
                ) : (
                  t(plan.ctaKey)
                )}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
